/**
 * Auto SpaceLink grouping (2026-10-02).
 *
 * Auto SpaceLink is the rule that links every scan at one address in one
 * program inside the VirtualSpace viewer, so a customer can move between
 * floors and between captures of the same building over time without a
 * new link. Discovery lives in `launch-siblings.ts`; this module holds the
 * pure helper that decides how a linked set is presented (the "same
 * address" match is in `spacelink-address.ts`):
 *
 *   - `groupSpaceLinks` — month/year sections for sets that span more than
 *     one capture month, newest first, floors ordered within each month.
 *
 * Kept free of Supabase and React so the viewer menu, the operator
 * SpaceLink panel and tests share one ordering.
 */

import { floorSortKey } from "@/lib/virtualspace/launch-siblings";

/** The fields grouping needs; `LaunchSibling` satisfies it. */
export type SpaceLinkMember = {
  deliverableId: string;
  label: string;
  /** Capture date (`YYYY-MM-DD`), or null when unknown. */
  capturedAt?: string | null;
};

export type SpaceLinkGroup<T extends SpaceLinkMember> = {
  /** `YYYY-MM`, or `undated`. */
  key: string;
  /** "September 2026", or "Undated". */
  label: string;
  members: T[];
};

export type SpaceLinkGrouping<T extends SpaceLinkMember> = {
  /** True when the set spans two or more capture months. The menu shows
   *  section headers only then; a single-month set (the floors of one
   *  capture) keeps today's flat floor list. */
  grouped: boolean;
  groups: SpaceLinkGroup<T>[];
  /** Every member in display order (groups flattened). */
  ordered: T[];
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const SHORT_MONTHS = MONTHS.map((m) => m.slice(0, 3));

const DATE_RE = /^(\d{4})-(\d{2})(?:-(\d{2}))?/;

/** `YYYY-MM` for a capture date, or null when it can't be read. */
export function captureMonthKey(capturedAt: string | null | undefined): string | null {
  const m = capturedAt ? DATE_RE.exec(capturedAt.trim()) : null;
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return `${m[1]}-${m[2]}`;
}

/** "September 2026" for `2026-09`; "Undated" otherwise. */
export function captureMonthLabel(key: string | null): string {
  const m = key ? /^(\d{4})-(\d{2})$/.exec(key) : null;
  if (!m) return "Undated";
  return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** "Sep 14, 2026" for a capture date; null when it can't be read. */
export function formatCaptureDate(capturedAt: string | null | undefined): string | null {
  const m = capturedAt ? DATE_RE.exec(capturedAt.trim()) : null;
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  const day = m[3] ? Number(m[3]) : null;
  return day
    ? `${SHORT_MONTHS[month - 1]} ${day}, ${m[1]}`
    : `${SHORT_MONTHS[month - 1]} ${m[1]}`;
}

const NATURAL_NAME_ORDER = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

function compareFloorAndName<T extends SpaceLinkMember>(a: T, b: T): number {
  const aKey = floorSortKey(a.label);
  const bKey = floorSortKey(b.label);
  if (aKey !== bKey) return aKey - bKey;
  const byLabel = NATURAL_NAME_ORDER.compare(a.label.trim(), b.label.trim());
  if (byLabel !== 0) return byLabel;
  // Repeated identical names: newest visit first, then a stable identity tie.
  return (b.capturedAt ?? "").localeCompare(a.capturedAt ?? "", "en") ||
    a.deliverableId.localeCompare(b.deliverableId, "en");
}

/** Flat default: physical floor signals, then natural names, across all dates. */
export function orderSpaceLinksByFloorAndName<T extends SpaceLinkMember>(members: readonly T[]): T[] {
  return [...members].sort(compareFloorAndName);
}

/**
 * Order a linked set for display.
 *
 * One capture month (or no dates at all): a single flat group in floor
 * order, exactly like the pre-SpaceLink menu. Two or more months: one
 * section per month, newest month first, undated scans last, floors in
 * order inside each month.
 */
export function groupSpaceLinks<T extends SpaceLinkMember>(
  members: ReadonlyArray<T>,
): SpaceLinkGrouping<T> {
  const byKey = new Map<string, T[]>();
  for (const member of members) {
    const key = captureMonthKey(member.capturedAt) ?? "undated";
    const bucket = byKey.get(key);
    if (bucket) bucket.push(member);
    else byKey.set(key, [member]);
  }
  const datedKeys = [...byKey.keys()].filter((k) => k !== "undated");
  const grouped = datedKeys.length >= 2;
  if (!grouped) {
    const flat = orderSpaceLinksByFloorAndName(members);
    return {
      grouped: false,
      groups: [{ key: datedKeys[0] ?? "undated", label: captureMonthLabel(datedKeys[0] ?? null), members: flat }],
      ordered: flat,
    };
  }
  const keys = datedKeys.sort((a, b) => b.localeCompare(a));
  if (byKey.has("undated")) keys.push("undated");
  const groups = keys.map((key) => ({
    key,
    label: captureMonthLabel(key === "undated" ? null : key),
    members: orderSpaceLinksByFloorAndName(byKey.get(key) ?? []),
  }));
  return { grouped: true, groups, ordered: groups.flatMap((g) => g.members) };
}

/**
 * Month/year section headers for a set already in display order, without
 * reordering it. Returns null (render a flat list) when the set covers a
 * single capture month, or when a month appears in more than one run,
 * which is how an operator-curated bundle's own order is left alone.
 */
export function sectionSpaceLinks<T extends SpaceLinkMember>(
  ordered: ReadonlyArray<T>,
): SpaceLinkGroup<T>[] | null {
  const sections: SpaceLinkGroup<T>[] = [];
  const seen = new Set<string>();
  for (const member of ordered) {
    const key = captureMonthKey(member.capturedAt) ?? "undated";
    const last = sections[sections.length - 1];
    if (last && last.key === key) {
      last.members.push(member);
      continue;
    }
    if (seen.has(key)) return null;
    seen.add(key);
    sections.push({
      key,
      label: captureMonthLabel(key === "undated" ? null : key),
      members: [member],
    });
  }
  const datedCount = sections.filter((s) => s.key !== "undated").length;
  return datedCount >= 2 ? sections : null;
}

/**
 * The date a scan is filed under, as `YYYY-MM-DD`: its visit. SpaceLab
 * (2026-10-02): imported scans go by their visit date and new ones by
 * their mission date, so the mission's actual start (the visit) wins,
 * then its scheduled start (the mission date). Matterport's own capture
 * date (column, then last payload) only covers scans with no mission
 * date, and the deliverable's creation is the last resort. Read-time
 * only: never written back, because `provider_captured_at` feeds
 * model-to-mission match scoring and must stay Matterport's value.
 */
export function pickCaptureDate(sources: {
  providerCapturedAt?: string | null;
  providerPayloadCreatedAt?: string | null;
  missionActualStartAt?: string | null;
  missionScheduledStartAt?: string | null;
  deliverableCreatedAt?: string | null;
}): string | null {
  for (const value of [
    sources.missionActualStartAt,
    sources.missionScheduledStartAt,
    sources.providerCapturedAt,
    sources.providerPayloadCreatedAt,
    sources.deliverableCreatedAt,
  ]) {
    const m = typeof value === "string" ? DATE_RE.exec(value.trim()) : null;
    if (m && m[3] && captureMonthKey(value)) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  return null;
}

/** Operator Mission date: use only a real mission start field. */
export function pickMissionDate(sources: {
  actual?: string | null;
  scheduled?: string | null;
} | null | undefined): string | null {
  for (const value of [sources?.actual, sources?.scheduled]) {
    if (typeof value !== "string") continue;
    const match = DATE_RE.exec(value.trim());
    if (!match?.[3] || !Number.isFinite(Date.parse(value))) continue;
    const day = `${match[1]}-${match[2]}-${match[3]}`;
    const parsed = Date.parse(`${day}T00:00:00Z`);
    if (Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === day) return day;
  }
  return null;
}
